// EFFETS MONDE du dash (22/07, « je veux les 3 de manière aléatoire ») : les 3 animations de la
// maquette /pages/dash-magie.html, dessinées en ESPACE MONDE par-dessus/dessous le coureur (le
// canvas natif du Baroudeur est trop petit pour une traînée de 3 cases). 100 % cosmétique client :
// l'état ici (départ du dash, échos, runes) n'entre jamais dans la simulation ni le hash. Le
// tirage de la variante reste dash-variant.ts (pur, partagé). Le crépitement (foudre, arcs) est
// re-seedé par PALIER de temps (mulberry sur floor(t)) : déterministe, zéro Math.random.
//   0 Comète de cristal : queue continue 3 nappes + échos du corps + pluie d'étoiles à l'arrivée.
//   1 Éclair d'arcane   : cercle runique, corps remplacé par une foudre dorée à branches, runes
//                         semées sur le trajet puis ASPIRÉES dans le héros à l'arrivée.
//   2 Onde tonnerre     : lignes d'aspiration, triple onde de choc au départ, arcs électriques,
//                         seconde onde à l'arrivée (l'étirement/ressort du corps est dans render).
import { drawBaroudeur } from './baroudeur'
import { dashVariant, type DashVariant } from './dash-variant'

const TS = 48
const TAU = Math.PI * 2
const AFTER_TICKS = 16 // durée des effets d'arrivée après dashUntil

interface DashFx {
  v: DashVariant
  until: number // dashUntil suivi (clé d'unicité du dash)
  charge: number // dashChargeUntil (le burst démarre là)
  x0: number; y0: number // départ (px du sprite, coin haut-gauche)
  x1: number; y1: number // position courante pendant le burst, puis arrivée figée
  face: 1 | -1
  hue: number
  runes: { x: number; y: number }[]
  ghosts: { x: number; y: number; at: number }[]
}
const store: (DashFx | null)[] = []

function mulberry(a: number): () => number {
  return function () { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296 }
}
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number): void {
  if (a <= 0.004 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(${rgb},${a})`); g.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill()
}
function star(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, a: number): void {
  if (a <= 0.004) return
  ctx.strokeStyle = `rgba(200,250,255,${a})`; ctx.lineWidth = 1.6; ctx.lineCap = 'round'
  ctx.beginPath(); ctx.moveTo(x - s, y); ctx.lineTo(x + s, y); ctx.moveTo(x, y - s); ctx.lineTo(x, y + s); ctx.stroke()
  glow(ctx, x, y, s * 1.5, '150,240,255', a * 0.5)
}
function glyph(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, i: number, a: number): void {
  if (a <= 0.004) return
  const rnd = mulberry(i * 71 + 13)
  ctx.strokeStyle = `rgba(255,210,63,${a})`; ctx.lineWidth = 1.4; ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(x + (rnd() - 0.5) * s * 2, y + (rnd() - 0.5) * s * 2)
  for (let k = 0; k < 4; k++) ctx.lineTo(x + (rnd() - 0.5) * s * 2, y + (rnd() - 0.5) * s * 2)
  ctx.stroke()
  glow(ctx, x, y, s * 2.2, '255,210,63', a * 0.35)
}

/** Suivi par frame, appelé depuis drawRunner (positions interpolées écran-monde du coureur). */
export function dashFxTrack(idx: number, dashUntil: number, dashChargeUntil: number, px: number, py: number, face: 1 | -1, hue: number, t: number): void {
  if (dashUntil <= t - AFTER_TICKS - 6) { if (store[idx] && t > store[idx]!.until + AFTER_TICKS + 4) store[idx] = null; return }
  const fx = store[idx]
  if (dashUntil > t) {
    if (!fx || fx.until !== dashUntil) {
      store[idx] = { v: dashVariant(dashUntil, idx), until: dashUntil, charge: dashChargeUntil, x0: px, y0: py, x1: px, y1: py, face, hue, runes: [], ghosts: [] }
      return
    }
    fx.x1 = px; fx.y1 = py; fx.face = face
    const p = Math.max(0, Math.min(1, (t - fx.charge) / 6))
    if (fx.v === 0) { // échos de la comète
      const last = fx.ghosts[fx.ghosts.length - 1]
      if (t > fx.charge && (!last || t - last.at > 1.1)) { fx.ghosts.push({ x: px, y: py, at: t }); if (fx.ghosts.length > 4) fx.ghosts.shift() }
    } else if (fx.v === 1) { // semer les runes aux fractions du trajet RÉEL
      if (t > fx.charge && p >= 0.28 * (fx.runes.length + 1) && fx.runes.length < 3) fx.runes.push({ x: px + TS / 2, y: py + TS * 0.3 })
    }
  } else if (fx && fx.until === dashUntil && t > fx.until + AFTER_TICKS + 4) store[idx] = null
}

/** Le corps doit-il être MASQUÉ ce frame ? (Éclair d'arcane : le héros EST la foudre.) */
export function dashFxHideBody(idx: number, dashUntil: number, t: number): boolean {
  const fx = store[idx]
  return !!fx && fx.until === dashUntil && fx.v === 1 && dashUntil > t && t >= fx.charge
}

/** Couche ARRIÈRE (sous les coureurs) : queue de comète, échos, aspiration et ondes de départ. */
export function drawDashFxBack(ctx: CanvasRenderingContext2D, t: number): void {
  for (const fx of store) {
    if (!fx) continue
    const bursting = fx.until > t && t >= fx.charge
    const cx1 = fx.x1 + TS / 2, cy1 = fx.y1 + TS * 0.42
    if (fx.v === 0 && bursting) {
      // queue continue 3 nappes (additive), du départ à la position courante
      const cx0 = fx.x0 + TS / 2
      if (Math.abs(cx1 - cx0) > 6) {
        ctx.save(); ctx.globalCompositeOperation = 'lighter'
        const layers: [number, string, number][] = [[22, '139,123,255', 0.16], [14, '106,252,255', 0.28], [7, '234,255,255', 0.4]]
        for (const [h, rgb, a] of layers) {
          const g = ctx.createLinearGradient(cx0, 0, cx1, 0)
          g.addColorStop(0, `rgba(${rgb},0)`); g.addColorStop(0.7, `rgba(${rgb},${a})`); g.addColorStop(1, `rgba(${rgb},${a * 1.15})`)
          ctx.fillStyle = g
          const wob = Math.sin(t * 0.8) * 2.5
          ctx.beginPath()
          ctx.moveTo(cx0, cy1 + wob)
          ctx.quadraticCurveTo((cx0 + cx1) / 2, cy1 - h * 0.55 + wob, cx1, cy1 - h * 0.32)
          ctx.lineTo(cx1, cy1 + h * 0.32)
          ctx.quadraticCurveTo((cx0 + cx1) / 2, cy1 + h * 0.55 + wob, cx0, cy1 + wob)
          ctx.closePath(); ctx.fill()
        }
        ctx.restore()
      }
      // échos du corps (répétitions lumineuses de la fente)
      for (const g of fx.ghosts) {
        const age = t - g.at
        if (age > 11) continue
        ctx.save(); ctx.globalAlpha = 0.36 * (1 - age / 11); ctx.globalCompositeOperation = 'lighter'
        drawBaroudeur(ctx, 'dash', 0.5, fx.face, g.x, g.y, TS, { dashVariant: fx.v, hue: fx.hue })
        ctx.restore()
      }
      glow(ctx, cx1, cy1, 30, '106,252,255', 0.2)
    }
    if (fx.v === 2) {
      const rnd = mulberry(Math.floor(t * 1.2) * 131 + fx.until)
      if (fx.until > t && t < fx.charge) { // aspiration : l'air converge
        for (let k = 0; k < 6; k++) {
          const side = k % 2 ? 1 : -1
          const lx = cx1 + side * (36 + rnd() * 46), ly = fx.y1 + 6 + rnd() * 34
          ctx.strokeStyle = 'rgba(106,252,255,0.32)'; ctx.lineWidth = 1.6
          ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx - side * (12 + rnd() * 10), ly); ctx.stroke()
        }
      }
      if (bursting || (t >= fx.until && t < fx.until + 6)) { // triple onde de choc au départ
        const age = t - fx.charge
        if (age < 9) {
          const cx0 = fx.x0 + TS / 2, cy0 = fx.y0 + TS * 0.6
          ctx.save(); ctx.globalCompositeOperation = 'lighter'
          const rings: [number, string, number, number][] = [[7, '240,255,255', 0.6, 5], [9.5, '106,252,255', 0.45, 3], [12, '106,252,255', 0.25, 1.5]]
          for (const [sp, rgb, a0, w] of rings) {
            const rr = 6 + age * sp, aa = a0 * (1 - age / 9)
            if (aa <= 0.01) continue
            ctx.strokeStyle = `rgba(${rgb},${aa})`; ctx.lineWidth = w * (1 - age / 12)
            ctx.beginPath(); ctx.ellipse(cx0, cy0, rr, rr * 0.7, 0, 0, TAU); ctx.stroke()
          }
          ctx.restore()
          if (age < 3) glow(ctx, cx0, cy0, 36, '240,255,255', 0.4 * (1 - age / 3))
        }
        if (bursting) for (let k = 0; k < 4; k++) { // lignes de vitesse derrière le corps
          const ly = fx.y1 + 8 + rnd() * 30, len = 20 + rnd() * 18
          ctx.strokeStyle = `rgba(${k % 2 ? '106,252,255' : '240,255,255'},0.35)`; ctx.lineWidth = 1.8
          ctx.beginPath(); ctx.moveTo(cx1 - fx.face * (14 + rnd() * 8), ly); ctx.lineTo(cx1 - fx.face * (14 + len), ly); ctx.stroke()
        }
      }
    }
  }
}

/** Couche AVANT (par-dessus les coureurs) : foudre + runes, arcs, étoiles, ondes d'arrivée. */
export function drawDashFxFront(ctx: CanvasRenderingContext2D, t: number): void {
  for (let i = 0; i < store.length; i++) {
    const fx = store[i]
    if (!fx) continue
    const bursting = fx.until > t && t >= fx.charge
    const after = t >= fx.until && t < fx.until + AFTER_TICKS
    const u = after ? (t - fx.until) / AFTER_TICKS : 0
    const cx1 = fx.x1 + TS / 2, cy1 = fx.y1 + TS * 0.42
    if (fx.v === 0 && after) {
      // pluie d'étoiles sur le trajet + anneau au sol à l'arrivée
      const rnd = mulberry(fx.until * 31 + 7)
      for (let k = 0; k < 8; k++) {
        const along = 0.3 + 0.7 * (k / 7)
        const sx = fx.x0 + (fx.x1 - fx.x0) * along + TS / 2 + (rnd() - 0.5) * 14
        const sy = fx.y0 + TS * 0.35 + (rnd() - 0.5) * 16 + u * 12
        const tw = 0.55 + 0.45 * Math.sin(t * 1.9 + k * 2.1)
        star(ctx, sx, sy, (3.5 + rnd() * 3) * (1 - u * 0.5), (1 - u) * tw)
      }
      if (u < 0.5) {
        ctx.save(); ctx.globalCompositeOperation = 'lighter'
        ctx.strokeStyle = `rgba(106,252,255,${0.55 * (1 - u * 2)})`; ctx.lineWidth = 3
        const rr = 8 + u * 72
        ctx.beginPath(); ctx.ellipse(cx1, fx.y1 + TS - 2, rr, rr * 0.3, 0, 0, TAU); ctx.stroke()
        ctx.restore()
      }
    }
    if (fx.v === 1) {
      if (fx.until > t && t < fx.charge) { // cercle runique de charge
        const k = 1 - Math.max(0, (fx.charge - t) / 2)
        const cx0 = fx.x0 + TS / 2, gy = fx.y0 + TS - 2
        ctx.save(); ctx.globalCompositeOperation = 'lighter'
        const spin = t * 0.24
        ctx.strokeStyle = `rgba(255,210,63,${0.5 * k})`; ctx.lineWidth = 1.8
        ctx.beginPath(); ctx.ellipse(cx0, gy, 22, 8, 0, spin, spin + TAU * 0.8); ctx.stroke()
        for (let g2 = 0; g2 < 3; g2++) { const ga = spin * 0.6 + (g2 / 3) * TAU; glyph(ctx, cx0 + Math.cos(ga) * 25, gy + Math.sin(ga) * 8, 4, g2 + 1, 0.8 * k) }
        ctx.restore()
      }
      if (bursting) {
        // la FOUDRE : trait doré à 3 épaisseurs + branches, du départ à la tête
        const x0 = fx.x0 + TS / 2, y = cy1
        const n = Math.max(2, Math.floor(Math.abs(cx1 - x0) / 14))
        const seed = Math.floor(t * 0.8)
        for (const [w, colr] of [[9, 'rgba(255,210,63,0.25)'], [4, 'rgba(255,210,63,0.65)'], [1.8, 'rgba(255,248,220,0.95)']] as [number, string][]) {
          const rnd = mulberry(seed * 977 + fx.until)
          ctx.save(); ctx.globalCompositeOperation = 'lighter'
          ctx.strokeStyle = colr; ctx.lineWidth = w; ctx.lineJoin = 'round'
          ctx.beginPath(); ctx.moveTo(x0, y)
          for (let k = 1; k <= n; k++) ctx.lineTo(x0 + (cx1 - x0) * (k / n), y + (k === n ? 0 : (rnd() - 0.5) * 16))
          ctx.stroke(); ctx.restore()
        }
        const rnb = mulberry(seed * 397 + fx.until)
        ctx.save(); ctx.globalCompositeOperation = 'lighter'
        ctx.strokeStyle = 'rgba(255,210,63,0.5)'; ctx.lineWidth = 1.3
        for (let b = 0; b < 2; b++) { // branches secondaires
          const bx = x0 + (cx1 - x0) * (0.25 + rnb() * 0.5), up = rnb() > 0.5 ? -1 : 1
          ctx.beginPath(); ctx.moveTo(bx, y)
          let yy = y
          for (let k = 0; k < 3; k++) { yy += up * (4 + rnb() * 7); ctx.lineTo(bx + fx.face * (k + 1) * (3 + rnb() * 5), yy) }
          ctx.stroke()
        }
        ctx.restore()
        glow(ctx, cx1, y, 15, '255,248,220', 0.8); glow(ctx, cx1, y, 30, '255,210,63', 0.45)
      }
      if (bursting || after) { // runes : flottent, puis sont ASPIRÉES une à une
        for (let k = fx.runes.length - 1; k >= 0; k--) {
          const rn = fx.runes[k]!
          if (after && u > 0.12 + k * 0.16) {
            rn.x += (cx1 - rn.x) * 0.3; rn.y += (cy1 - rn.y) * 0.3
            if (Math.abs(rn.x - cx1) < 6 && Math.abs(rn.y - cy1) < 6) { fx.runes.splice(k, 1); glow(ctx, cx1, cy1, 26, '255,210,63', 0.55); continue }
          }
          ctx.save(); ctx.globalCompositeOperation = 'lighter'
          glyph(ctx, rn.x, rn.y + Math.sin(t * 0.28 + k * 2.4) * 2.5, 5, k + 4, after ? 0.9 * (1 - u * 0.5) : 0.9)
          ctx.restore()
        }
        if (after && u < 0.4) { // anneau d'or qui se referme sur le héros
          ctx.save(); ctx.globalCompositeOperation = 'lighter'
          ctx.strokeStyle = `rgba(255,210,63,${0.6 * (1 - u * 2.5)})`; ctx.lineWidth = 2.5
          const rr = 38 * (1 - u * 2.5) + 8
          ctx.beginPath(); ctx.ellipse(cx1, cy1, rr, rr * 0.8, 0, 0, TAU); ctx.stroke()
          ctx.restore()
        }
      }
    }
    if (fx.v === 2) {
      if (bursting) { // arcs électriques qui crépitent le long du corps
        const seed = Math.floor(t * 0.9)
        for (let arc = 0; arc < 3; arc++) {
          const rnd = mulberry(seed * 131 + arc * 37 + fx.until)
          ctx.save(); ctx.globalCompositeOperation = 'lighter'
          ctx.strokeStyle = arc === 0 ? 'rgba(240,255,255,0.8)' : 'rgba(106,252,255,0.55)'
          ctx.lineWidth = arc === 0 ? 1.7 : 1.2; ctx.lineJoin = 'round'
          ctx.beginPath()
          let ax = cx1 - fx.face * 20, ay = fx.y1 + 6 + rnd() * 28
          ctx.moveTo(ax, ay)
          for (let k = 0; k < 4; k++) { ax += fx.face * (6 + rnd() * 8); ay += (rnd() - 0.5) * 15; ctx.lineTo(ax, ay) }
          ctx.stroke(); ctx.restore()
        }
        glow(ctx, cx1, cy1, 26, '106,252,255', 0.2)
      }
      if (after && u < 0.5) { // seconde onde, plus petite, à l'arrivée
        ctx.save(); ctx.globalCompositeOperation = 'lighter'
        ctx.strokeStyle = `rgba(106,252,255,${0.5 * (1 - u * 2)})`; ctx.lineWidth = 2.5
        const rr = 6 + u * 56
        ctx.beginPath(); ctx.ellipse(cx1, fx.y1 + TS * 0.6, rr, rr * 0.6, 0, 0, TAU); ctx.stroke()
        ctx.restore()
      }
    }
  }
}
